import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import useHandleError from '@/hooks/useHandleError';
import { axiosClient } from '@/services/BaseApi.service';

import type {
  MessageTemplateDTO,
  RecommendationTemplateDTO,
  TicketTemplateType
} from './AdministrationTemplates.types';

export const useMutationTemplate = (
  refetchTickets: () => void,
  refetchRecommendations: () => void,
  refetchMessages: () => void
) => {
  const handleError = useHandleError();

  return useMutation({
    mutationFn: (editTemplateInfo: {
      data: TicketTemplateType | MessageTemplateDTO | RecommendationTemplateDTO;
      id: number;
      templateType: string;
    }) => {
      const updatedInfo = editTemplateInfo.data;
      const { id } = editTemplateInfo;
      const { templateType } = editTemplateInfo;

      return axiosClient.put(`/templates/${templateType}/${id}`, updatedInfo);
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update template.');
    },
    onSuccess: (data) => {
      if (data.config?.url?.includes('ticket')) {
        refetchTickets();
      } else if (data.config?.url?.includes('recommendation')) {
        refetchRecommendations();
      } else {
        refetchMessages();
      }
    }
  });
};

export const useCreateTemplate = (
  refetchTickets: () => void,
  refetchRecommendations: () => void,
  refetchMessages: () => void
) => {
  const handleError = useHandleError();

  return useMutation({
    mutationFn: (createInfo: {
      templateType: string;
      data: TicketTemplateType | MessageTemplateDTO | RecommendationTemplateDTO;
    }) => {
      return axiosClient.post(
        `/templates/${createInfo.templateType}`,
        createInfo.data
      );
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to create template.');
    },
    onSuccess: (data) => {
      if (data.config?.url?.includes('ticket')) {
        refetchTickets();
      } else if (data.config?.url?.includes('recommendation')) {
        refetchRecommendations();
      } else {
        refetchMessages();
      }
    }
  });
};

export const useDeleteTemplate = (
  refetchTickets: () => void,
  refetchRecommendations: () => void,
  refetchMessages: () => void
) => {
  const handleError = useHandleError();
  return useMutation({
    mutationFn: (deleteInfo: { type: string; id: number }) => {
      return axiosClient.delete(
        `/templates/${deleteInfo.type}/${deleteInfo.id}`
      );
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to delete template.');
    },
    onSuccess: (data) => {
      if (data.config?.url?.includes('ticket')) {
        refetchTickets();
      } else if (data.config?.url?.includes('recommendation')) {
        refetchRecommendations();
      } else {
        refetchMessages();
      }
    }
  });
};
